// useEscapeProgress.js
// Frontend-only state machine for the main room: which activity is
// unlocked, which are completed, and which popup (initial clue / activity /
// learning / next clue) is currently on screen. Mirrors room.php's exact
// sequence: activity Submit -> learning popup -> close -> next clue popup ->
// close -> next object unlocks. This hook itself makes no backend calls —
// GamePage.jsx (via useEscapePersistence) saves the resulting state.
//
// Phase 5 resume: `initialUnlockedIndex`/`initialCompleted` seed a resumed
// run's unlock/completion state, and `skipInitialPopup` suppresses the
// "initial clue" popup on resume (only a brand-new game shows it) — the
// resumed player always lands on the room with their next activity unlocked
// and no popup open, which is the nearest safe/accurate state since
// mid-activity selections are intentionally not persisted (see Phase 5 report).
//
// ESC-QA-10 fix: the backend records an activity's completion (and advances
// currentActivity) the instant it's submitted — before the player has seen
// that activity's Learning popup or the next object's Clue popup. If the
// tab is refreshed while either popup is still open, the resume logic above
// used to unconditionally show no popup, silently unlocking the next object
// with its mandatory narrative never shown. A small marker — "which
// activity's Learning/Clue narrative is still owed" — is now kept in
// sessionStorage (scoped to this exact gameRunId), set the instant an
// activity completes and cleared only once its full narrative sequence is
// closed. On resume, if that marker still names the most recently
// *backend*-completed activity, the room re-enters directly at that
// activity's Learning popup instead of silently skipping to the room view.
// Never re-awards score (completion already happened server-side and is
// idempotent) and never reopens an already-completed activity's puzzle —
// only replays the narrative popups.
import { useCallback, useEffect, useReducer } from 'react';
import { ROOM_OBJECT_IDS } from '../data/gameText';

const NARRATIVE_PENDING_KEY = 'escapeNarrativePendingActivity';

function readPendingNarrativeActivityId(gameRunId) {
  if (!gameRunId) return null;
  try {
    const raw = sessionStorage.getItem(NARRATIVE_PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.gameRunId === gameRunId ? parsed.activityId || null : null;
  } catch {
    return null;
  }
}

function writePendingNarrativeActivityId(gameRunId, activityId) {
  if (!gameRunId) return;
  try {
    if (activityId == null) {
      sessionStorage.removeItem(NARRATIVE_PENDING_KEY);
    } else {
      sessionStorage.setItem(NARRATIVE_PENDING_KEY, JSON.stringify({ gameRunId, activityId }));
    }
  } catch {
    // sessionStorage unavailable (private mode, quota, etc.) — the
    // narrative-skip protection this enables is best-effort only; normal
    // gameplay (scoring/persistence) is entirely unaffected.
  }
}

function buildInitialState({ gameRunId, initialUnlockedIndex = 0, initialCompleted = [], skipInitialPopup = false } = {}) {
  let activePopup = null;
  if (!skipInitialPopup) {
    activePopup = { type: 'initial' };
  } else {
    const pendingId = readPendingNarrativeActivityId(gameRunId);
    const lastCompleted = initialCompleted.length ? initialCompleted[initialCompleted.length - 1] : null;
    if (pendingId && pendingId === lastCompleted) {
      activePopup = { type: 'learning', id: pendingId };
    }
  }
  return {
    activePopup,
    unlockedIndex: initialUnlockedIndex,
    completed: initialCompleted,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case 'CLOSE_INITIAL':
      return { ...state, activePopup: null };

    case 'OPEN_ACTIVITY': {
      const idx = ROOM_OBJECT_IDS.indexOf(action.id);
      const unlocked = idx !== -1 && idx <= state.unlockedIndex;
      const alreadyCompleted = state.completed.includes(action.id);
      if (!unlocked || alreadyCompleted) return state;
      return { ...state, activePopup: { type: 'activity', id: action.id } };
    }

    case 'COMPLETE_ACTIVITY': {
      if (state.completed.includes(action.id)) return state;
      return {
        ...state,
        completed: [...state.completed, action.id],
        activePopup: { type: 'learning', id: action.id },
      };
    }

    case 'CLOSE_LEARNING': {
      const id = state.activePopup?.id;
      const idx = ROOM_OBJECT_IDS.indexOf(id);
      const nextId = ROOM_OBJECT_IDS[idx + 1];
      if (!nextId) {
        return { ...state, activePopup: null };
      }
      return { ...state, activePopup: { type: 'clue', id: nextId } };
    }

    case 'CLOSE_CLUE': {
      const id = state.activePopup?.id;
      const idx = ROOM_OBJECT_IDS.indexOf(id);
      if (idx === -1) return { ...state, activePopup: null };
      return { ...state, activePopup: null, unlockedIndex: Math.max(state.unlockedIndex, idx) };
    }

    default:
      return state;
  }
}

export function useEscapeProgress({
  onGameComplete,
  gameRunId,
  initialUnlockedIndex,
  initialCompleted,
  skipInitialPopup,
} = {}) {
  const [state, dispatch] = useReducer(
    reducer,
    { gameRunId, initialUnlockedIndex, initialCompleted, skipInitialPopup },
    buildInitialState
  );

  // A page load that resumes directly into an owed Learning popup (see
  // module doc comment above) still needs the marker to exist for the
  // *rest* of that sequence (e.g. a second refresh while still on
  // Learning/Clue must resume the same way) — buildInitialState only reads
  // it, so re-assert it once on mount for this exact case.
  useEffect(() => {
    if (state.activePopup?.type === 'learning' && state.completed.includes(state.activePopup.id)) {
      writePendingNarrativeActivityId(gameRunId, state.activePopup.id);
    }
    // Only ever needs to run once, for the initial resume snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeInitialPopup = useCallback(() => dispatch({ type: 'CLOSE_INITIAL' }), []);
  const openActivity = useCallback((id) => dispatch({ type: 'OPEN_ACTIVITY', id }), []);

  const completeActivity = useCallback(
    (id) => {
      writePendingNarrativeActivityId(gameRunId, id);
      dispatch({ type: 'COMPLETE_ACTIVITY', id });
    },
    [gameRunId]
  );

  const closeLearningPopup = useCallback(() => {
    const id = state.activePopup?.id;
    const isLast = ROOM_OBJECT_IDS.indexOf(id) === ROOM_OBJECT_IDS.length - 1;
    if (isLast) {
      // No Clue popup follows the last activity's Learning popup — this is
      // the final step of its narrative sequence.
      writePendingNarrativeActivityId(gameRunId, null);
    }
    dispatch({ type: 'CLOSE_LEARNING' });
    if (isLast) {
      onGameComplete?.();
    }
  }, [state.activePopup, onGameComplete, gameRunId]);

  const closeCluePopup = useCallback(() => {
    // Closing Clue is always the final step of the *previous* activity's
    // narrative sequence — safe to clear unconditionally.
    writePendingNarrativeActivityId(gameRunId, null);
    dispatch({ type: 'CLOSE_CLUE' });
  }, [gameRunId]);

  const isUnlocked = useCallback(
    (id) => {
      const idx = ROOM_OBJECT_IDS.indexOf(id);
      return idx !== -1 && idx <= state.unlockedIndex;
    },
    [state.unlockedIndex]
  );

  const isCompleted = useCallback((id) => state.completed.includes(id), [state.completed]);

  const isGameComplete = state.completed.length === ROOM_OBJECT_IDS.length;

  return {
    activePopup: state.activePopup,
    completed: state.completed,
    isUnlocked,
    isCompleted,
    isGameComplete,
    closeInitialPopup,
    openActivity,
    completeActivity,
    closeLearningPopup,
    closeCluePopup,
  };
}
